import { useEffect, useState } from 'react';
import { groupBy } from 'lodash';

import { UseEntityFormParams, UseEntityFormResult } from './useEntityForm.types';
import { useSubmitForm } from './useSubmitForm';
import { extractEntityFormApiErrors } from '../utils/entityFormApiErrorHandling';

export const useEntityForm = <T>({
  initialData,
  fields,
  submitFn,
  redirectUrl,
  groupOrder,
  groupIcons,
  icon,
  title,
  backUrl,
  headerActions,
  onSuccess,
  onSuccessWithResponse,
  redirectDelay,
  showSuccessBeforeRedirect,
  mutationStatus,
  errorExtractor,
}: UseEntityFormParams<T>): UseEntityFormResult<T> => {
  const [formData, setFormData] = useState<T>(initialData);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [submitErrorMessage, setSubmitErrorMessage] = useState<string | undefined>(undefined);

  const { handleSubmit: submitForm } = useSubmitForm<T>(submitFn, {
    onSuccess,
    onSuccessWithResponse,
    onError: (error) => {
      const extractErrors = errorExtractor ?? extractEntityFormApiErrors;
      const extractedErrors = extractErrors(
        error,
        'Nie udało się zapisać formularza. Zweryfikuj dane i spróbuj ponownie.'
      );

      setFieldErrors(extractedErrors.fieldErrors);
      setSubmitErrorMessage(extractedErrors.submitErrorMessage);
    },
    redirectPath: redirectUrl,
    redirectDelay,
    showSuccessBeforeRedirect,
  });
  const {
    isLoading = false,
    isError = false,
    isSuccess = false,
    error = undefined,
  } = mutationStatus ?? {};

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>, shouldRedirect = true) => {
    setFieldErrors({});
    setSubmitErrorMessage(undefined);
    submitForm(e, formData, shouldRedirect);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement> | any,
    _child?: React.ReactNode
  ) => {
    const { name, value } = e.target;
    const fieldName = String(name);

    setSubmitErrorMessage(undefined);
    setFieldErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const next = { ...prev };
      delete next[fieldName];
      return next;
    });

    setFormData((prev) => ({ ...prev, [name]: value }));
  };
  const setFieldValue = <K extends keyof T>(name: K, value: T[K]) => {
    const fieldName = String(name);

    setSubmitErrorMessage(undefined);
    setFieldErrors((prev) => {
      if (!prev[fieldName]) {
        return prev;
      }

      const next = { ...prev };
      delete next[fieldName];
      return next;
    });

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const groupedFields = groupBy(fields, 'group') as Record<string, any>;
  useEffect(() => {
    setFormData(initialData);
    setFieldErrors({});
    setSubmitErrorMessage(undefined);
  }, [initialData]);

  return {
    formConfig: { icon, title, backUrl, headerActions },
    formState: {
      formData,
      groupedFields,
      groupOrder,
      groupIcons,
    },
    formHandlers: {
      handleSubmit,
      onChange: handleChange,
      isLoading: isLoading, // jeśli chcesz – możesz dodać `isLoading` z useMutation
      isSuccess: isSuccess,
      isError: isError || !!submitErrorMessage,
      error: error,
      errorMessage: submitErrorMessage,
      fieldErrors,
      setFieldValue,
    },
    setFormData,
  };
};
